import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLicense } from "../../context/licenseContext";
import { useLocation_ } from "../../context/locationContext";
import "./Dashboard.css";
import CreateProductModal from "../../components/CreateProductModal/CreateProductModal";
import Button from "../../components/ui/Button";
import COAStagedUploadModal from "../../components/COAStagedUploadModal/COAStagedUploadModal";
import Loading from "../../components/Loading/Loading";
import DashboardFilterPanel from "../../components/DashboardFilterPanel/DashboardFilterPanel";
import CoaToShowroom from "../../components/CoaToShowroom/CoaToShowroom";
import TransferRequestsModal from "../../components/TransferRequestsModal/TransferRequestsModal";
import { useNavigate } from "react-router-dom";
import apiClient from "../../api/apiClient";
import { TERPENE_KEYS, CANNABINOID_KEYS, isCOAComplete, isWholesaleEligible } from "../../utils/coaPanelStatus";
import { exportProductData } from "../../utils/exportProductData";
import ExportModal from "../../components/ExportModal/ExportModal";
import WeighInModal from "../../components/WeighInModal/WeighInModal";
import { QRCodeSVG } from "qrcode.react";
import { getColor } from "../../utils/colorfunctions";
import { toSlug, BADGE_META } from "../../utils/badgeMeta";
import { mixDisplayName } from "../../utils/mixDisplayName";
import GenerateMenuModal from "../../components/GenerateMenuModal/GenerateMenuModal";
import RequestProductsModal from "../../components/RequestProductsModal/RequestProductsModal";
import TransferWeightModal from "../../components/TransferWeightModal/TransferWeightModal";
import { useTutorial } from "../../context/tutorialContext";
import { useCoaJobs } from "../../context/coaJobsContext";
import InvoiceUploadModal from "../../components/InvoiceUploadModal/InvoiceUploadModal";
import LocationTransfersDrawer from "../../components/LocationTransfersDrawer/LocationTransfersDrawer";
import CreatePreRollModal from "../../components/CreatePreRollModal/CreatePreRollModal";
import CreateMixModal from "../../components/CreateMixModal/CreateMixModal";
import InventoryTable from "./components/InventoryTable";
import ColumnPickerModal from "./components/ColumnPickerModal";
import useColumnPreferences from "./hooks/useColumnPreferences";

// ── Color order for the distribution bar ─────────────────────────────────────

const COLOR_ORDER = [
    { key: "yellow", label: "Yellow" },
    { key: "green", label: "Green" },
    { key: "blue", label: "Blue" },
    { key: "navy", label: "Navy" },
    { key: "purple", label: "Purple" },
];

// ── Calculated field helpers ──────────────────────────────────────────────────

const safeNum = (v) => parseFloat(v) || 0;
const calcTotalTerps = (c) => TERPENE_KEYS.reduce((s, k) => s + safeNum(c[k]), 0);

// ── Quantity helpers ──────────────────────────────────────────────────────────

function deriveQuantityMeta(product, hasLocationFilter) {
    if (hasLocationFilter) {
        const v = parseFloat(product.location_quantity);
        return {
            displayQty: isNaN(v) ? null : v,
            displayUnit: product.location_unit || product.weight_unit || null,
            isLicenseView: false,
            breakdown: null,
        };
    }
    let inv = product.location_inventory;
    if (typeof inv === "string") {
        try { inv = JSON.parse(inv); } catch { inv = []; }
    }
    const validSlots = (Array.isArray(inv) ? inv : []).filter(
        (i) => i && i.locationId != null && parseFloat(i.quantity) > 0
    );
    const unassigned = parseFloat(product.quantity) || 0;
    const locationSum = validSlots.reduce((s, i) => s + (parseFloat(i.quantity) || 0), 0);
    const total = unassigned + locationSum;
    return {
        displayQty: product.quantity != null || validSlots.length > 0 ? total : null,
        displayUnit: product.weight_unit || null,
        isLicenseView: true,
        breakdown: {
            unassigned,
            locations: validSlots.map((i) => ({
                locationId: i.locationId,
                locationName: i.locationName,
                quantity: parseFloat(i.quantity) || 0,
                unit: i.weight_unit || product.weight_unit || null,
            })),
        },
    };
}

// ── Filter helpers ────────────────────────────────────────────────────────────

function loadFiltersFromSession() {
    return {
        searchTerm:          localStorage.getItem("dashboard_filter_searchTerm")    || "",
        selectedColor:       localStorage.getItem("dashboard_filter_color")         || "",
        selectedProductType: localStorage.getItem("dashboard_filter_productType")   || "",
        fullPanelFilter:     localStorage.getItem("dashboard_filter_fullPanel")     || "",
        startDate:           localStorage.getItem("dashboard_filter_startDate")     || "",
        endDate:             localStorage.getItem("dashboard_filter_endDate")       || "",
    };
}

// ── ConfirmDialog ─────────────────────────────────────────────────────────────

function ConfirmDialog({ message, onConfirm, onCancel }) {
    return (
        <div className="confirm-dialog-overlay" onClick={onCancel}>
            <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
                <p className="confirm-dialog__message">{message}</p>
                <div className="confirm-dialog__actions">
                    <button className="confirm-dialog__btn confirm-dialog__btn--danger" onClick={onConfirm}>
                        Remove
                    </button>
                    <button className="confirm-dialog__btn confirm-dialog__btn--cancel" onClick={onCancel}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── QRModal ───────────────────────────────────────────────────────────────────

const BADGE_COLORS = ["blue", "green", "navy", "purple", "yellow", "no color", "mix"];

function QRModal({ licenseNo, companyName, locationId, onClose }) {
    const origin = window.location.origin;
    const slug   = toSlug(companyName);
    const qrRefs = useRef({});

    const [productQueue, setProductQueue]           = useState([]);
    const [pendingProductDownload, setPending]       = useState(false);
    const [isDownloadingProducts, setIsDownloading] = useState(false);
    const productQrRefs = useRef({});

    const urlForColor   = (color) => color === "mix"
        ? `${origin}/compliance/${slug}?type=mix`
        : `${origin}/compliance/${slug}?color=${color}`;
    const urlForProduct = (product) => {
        const pt = (product.product_type || '').toLowerCase();
        return (pt.startsWith('mix') || pt === 'pre-roll')
            ? `${origin}/compliance/${slug}/mix/${product.id}`
            : `${origin}/compliance/${slug}?product=${product.id}`;
    };

    const downloadBadge = async (color) => {
        const qrEl = qrRefs.current[color]?.querySelector("svg");
        if (!qrEl) return;

        const meta = BADGE_META[color];
        const variant = meta.noStrain;
        const res = await fetch(`/Badges/${variant.file}`);
        const badgeText = await res.text();

        const parser = new DOMParser();
        const badgeDoc = parser.parseFromString(badgeText, "image/svg+xml");
        const badgeSvg = badgeDoc.querySelector("svg");

        const qrClone = qrEl.cloneNode(true);
        qrClone.setAttribute("x", String(variant.qx));
        qrClone.setAttribute("y", String(variant.qy));
        qrClone.setAttribute("width", String(variant.qs));
        qrClone.setAttribute("height", String(variant.qs));
        badgeSvg.appendChild(badgeDoc.importNode(qrClone, true));

        const svgStr = new XMLSerializer().serializeToString(badgeSvg);
        const blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `compliance-badge-${color}-${licenseNo}.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const downloadAll = async () => {
        for (const color of BADGE_COLORS) {
            await downloadBadge(color);
        }
    };

    const downloadAllProductBadges = async () => {
        setIsDownloading(true);
        try {
            const locationParam = locationId ? `&locationId=${locationId}` : "";
            const res = await apiClient.get(`/products?licenseNo=${licenseNo}${locationParam}`);
            const data = Array.isArray(res.data) ? res.data : [];
            const eligible = data.filter((p) => {
                const pt = (p.product_type || '').toLowerCase();
                const badgeColor = (pt.startsWith('mix') || pt === 'pre-roll') ? 'mix' : (p.color || '').toLowerCase();
                return BADGE_META[badgeColor] && p.quantity > 0;
            });
            if (eligible.length === 0) { setIsDownloading(false); return; }
            setProductQueue(eligible);
            setPending(true);
        } catch (err) {
            console.error("Failed to fetch products for badge download", err);
            setIsDownloading(false);
        }
    };

    useEffect(() => {
        if (!pendingProductDownload || productQueue.length === 0) return;

        const doDownload = async () => {
            for (const product of productQueue) {
                const pt = (product.product_type || '').toLowerCase();
                const color = (pt.startsWith('mix') || pt === 'pre-roll') ? 'mix' : (product.color || '').toLowerCase();
                const meta = BADGE_META[color];
                if (!meta) continue;

                const qrEl = productQrRefs.current[product.id]?.querySelector("svg");
                if (!qrEl) continue;

                const variant = meta.strain;
                const res = await fetch(`/Badges/${variant.file}`);
                const badgeText = await res.text();
                const parser = new DOMParser();
                const badgeDoc = parser.parseFromString(badgeText, "image/svg+xml");
                const badgeSvg = badgeDoc.querySelector("svg");

                if (product.product_name) {
                    const NS = "http://www.w3.org/2000/svg";
                    const textEl = badgeDoc.createElementNS(NS, "text");
                    textEl.setAttribute("x", "51.03");
                    textEl.setAttribute("y", "27.5");
                    textEl.setAttribute("text-anchor", "middle");
                    textEl.setAttribute("dominant-baseline", "central");
                    textEl.setAttribute("font-family", "Arial, Helvetica, sans-serif");
                    textEl.setAttribute("font-weight", "bold");
                    textEl.setAttribute("fill", "#231f20");
                    const name = mixDisplayName(product.product_name, product.product_type, product.created_at);
                    const fontSize = name.length > 20 ? 3 : name.length > 12 ? 3.8 : 4.5;
                    textEl.setAttribute("font-size", String(fontSize));
                    textEl.textContent = name;
                    badgeSvg.appendChild(textEl);
                }

                const qrClone = qrEl.cloneNode(true);
                qrClone.setAttribute("x", String(variant.qx));
                qrClone.setAttribute("y", String(variant.qy));
                qrClone.setAttribute("width", String(variant.qs));
                qrClone.setAttribute("height", String(variant.qs));
                badgeSvg.appendChild(badgeDoc.importNode(qrClone, true));

                const svgStr = new XMLSerializer().serializeToString(badgeSvg);
                const blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const badgeFileName = mixDisplayName(product.product_name, product.product_type, product.created_at) || "badge";
                a.download = `${badgeFileName.replace(/\s+/g, "-")}-badge.svg`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }
            setPending(false);
            setIsDownloading(false);
            setProductQueue([]);
        };

        doDownload();
    }, [productQueue, pendingProductDownload]);

    return (
        <div className="qr-modal-overlay" onClick={onClose}>
            <div className="qr-modal qr-modal--badges" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Compliance Badge QR Codes">
                <div className="qr-modal__header">
                    <h3 className="qr-modal__title">Compliance Badge QR Codes</h3>
                    <button className="qr-modal__close" onClick={onClose} aria-label="Close">✕</button>
                </div>
                <div className="qr-modal__body">
                    <p className="qr-modal__company">{companyName}</p>
                    <p className="qr-modal__hint">Each badge links to your compliance page filtered by color.</p>
                    <div className="qr-modal__badge-grid">
                        {BADGE_COLORS.map((color) => {
                            const meta = BADGE_META[color];
                            return (
                            <div key={color} className="qr-modal__badge-card" style={color === "no color" ? { justifyContent: "flex-end" } : undefined}>
                                <svg
                                    className="qr-modal__badge-preview"
                                    viewBox={meta.vb}
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ width: "100%", height: "auto", display: "block" }}
                                >
                                    <image href={`/Badges/${meta.noStrain.file}`} x="0" y="0" width={meta.w} height={meta.h} />
                                    <foreignObject x={meta.noStrain.qx} y={meta.noStrain.qy} width={meta.noStrain.qs} height={meta.noStrain.qs}>
                                        <div xmlns="http://www.w3.org/1999/xhtml" ref={(el) => { qrRefs.current[color] = el; }}>
                                            <QRCodeSVG value={urlForColor(color)} size={meta.noStrain.qs} level="M" includeMargin={false} fgColor="#0f172a" bgColor="#ffffff" />
                                        </div>
                                    </foreignObject>
                                </svg>
                                <span className="qr-modal__badge-label">{color === "no color" ? "Non-Flower" : color === "mix" ? "Mixes / Pre-Rolls" : color.charAt(0).toUpperCase() + color.slice(1)}</span>
                                <button className="qr-modal__btn qr-modal__btn--download" onClick={() => downloadBadge(color)}>
                                    Download
                                </button>
                            </div>
                        );
                        })}
                    </div>
                    {productQueue.map((product) => {
                        const pt = (product.product_type || '').toLowerCase();
                        const badgeColor = (pt.startsWith('mix') || pt === 'pre-roll') ? 'mix' : (product.color || '').toLowerCase();
                        if (!BADGE_META[badgeColor]) return null;
                        return (
                            <div
                                key={product.id}
                                ref={(el) => { productQrRefs.current[product.id] = el; }}
                                style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
                            >
                                <QRCodeSVG
                                    value={urlForProduct(product)}
                                    size={108}
                                    level="M"
                                    includeMargin={false}
                                    fgColor="#0f172a"
                                    bgColor="#ffffff"
                                />
                            </div>
                        );
                    })}
                </div>
                <div className="qr-modal__footer">
                    <button className="qr-modal__btn qr-modal__btn--cancel" onClick={onClose}>Close</button>
                    <button className="qr-modal__btn qr-modal__btn--download" onClick={downloadAll}>Download All 7</button>
                    <button
                        className="qr-modal__btn qr-modal__btn--download"
                        onClick={downloadAllProductBadges}
                        disabled={isDownloadingProducts}
                    >
                        {isDownloadingProducts ? "Downloading..." : "Download All Product Badges"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

const Dashboard = ({ isLoading, userId, setHeaderTitle, setCOAProcessing }) => {
    const { currentLicense, licensesLoaded } = useLicense();
    const { currentLocation, locations } = useLocation_();
    const { checkProductsTutorial, currentPhase } = useTutorial();
    const { registerRefreshCallback } = useCoaJobs();
    const [coas, setCoas] = useState([]);
    const [productTypes, setProductTypes] = useState([]);
    const [colors, setColors] = useState([]);
    const [loadProducts, setLoadProducts] = useState(true);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [title, setTitle] = useState("");
    const [myPermissions, setMyPermissions] = useState(() => {
        try {
            const saved = localStorage.getItem("currentPermissions");
            return saved ? JSON.parse(saved) : null;
        } catch {
            return null;
        }
    });

    // ── Filter state ──────────────────────────────────────────────────────────
    const [appliedFilters, setAppliedFilters] = useState(() => loadFiltersFromSession());

    // ── Filter panel state ────────────────────────────────────────────────────
    const [panelOpen, setPanelOpen] = useState(() => localStorage.getItem("dashboard_panel_open") !== "false");

    // ── Column preferences ────────────────────────────────────────────────────
    const { columns: activeColumns, setColumns: setActiveColumns, resetToDefaults } = useColumnPreferences();
    const [showColumnPicker, setShowColumnPicker] = useState(false);

    // ── Publish/unpublish state ───────────────────────────────────────────────
    const [publishModalCoa, setPublishModalCoa] = useState(null);
    const [unpublishConfirmCoa, setUnpublishConfirmCoa] = useState(null);

    // ── Create Product modal state ─────────────────────────────────────────────
    const [showCreateProductModal, setShowCreateProductModal] = useState(false);
    const [showStagedUploadModal, setShowStagedUploadModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);
    const [showGenerateMenuModal, setShowGenerateMenuModal] = useState(false);

    // ── Weigh-In modal state ───────────────────────────────────────────────────
    const [showWeighInModal, setShowWeighInModal] = useState(false);

    // ── Transfer requests state (marketplace only) ─────────────────────────────
    const [showTransferRequestsModal, setShowTransferRequestsModal] = useState(false);
    const [pendingRequestCount, setPendingRequestCount] = useState(0);
    // ── Location product request state ────────────────────────────────────────
    const [showRequestProductsModal, setShowRequestProductsModal] = useState(false);
    const [showInvoiceUploadModal, setShowInvoiceUploadModal] = useState(false);
    const [pendingLocationRequestCount, setPendingLocationRequestCount] = useState(0);
    // ── Location transfers drawer state ───────────────────────────────────────
    const [showLocationTransfersDrawer, setShowLocationTransfersDrawer] = useState(false);

    // ── Pre-roll / Mix modal state ──────────────────────────────────────────
    const [preRollSource, setPreRollSource] = useState(null);
    const [showCreateMixModal, setShowCreateMixModal] = useState(false);

    // ── Location transfer modal state ─────────────────────────────────────────
    const [locationTransfer, setLocationTransfer] = useState(null);
 
    const navigate = useNavigate(); 

    const fetchProducts = async () => {
        if (!licensesLoaded) return;
        setLoadProducts(true);
        setIsInitialLoading(true);
        if (!currentLicense?.licenseNo) {
            setLoadProducts(false);
            setIsInitialLoading(false);
            setCoas([]);
            return;
        }
        try {
            setTitle(currentLicense.title);
            const locationParam = currentLocation?.locationId ? `&locationId=${currentLocation.locationId}` : "";
            const res = await apiClient.get(`/products?licenseNo=${currentLicense.licenseNo}${locationParam}`);
            const data = Array.isArray(res.data) ? res.data : [];
            const hasLocationFilter = !!currentLocation?.locationId;
            const enriched = data.map((p) => ({
                ...p,
                _quantityMeta: deriveQuantityMeta(p, hasLocationFilter),
            }));
            setCoas(enriched);
            setProductTypes([
                ...new Set(enriched.filter((p) => p.product_type).map((p) => p.product_type.toLowerCase())),
            ]);
            setColors([...new Set(enriched.filter((p) => {
                const pt = (p.product_type || "").toLowerCase();
                return p.color && (pt === "flower" || pt === "pre-roll");
            }).map((p) => p.color.toLowerCase()))]);
        } catch (error) {
            console.error("Error fetching products:", error);
            setCoas([]);
        } finally {
            setLoadProducts(false);
            setIsInitialLoading(false);
        }
    };
 
    const fetchCoas = fetchProducts;

    useEffect(() => {
        return registerRefreshCallback(fetchProducts);
    }, [registerRefreshCallback]);

    useEffect(() => {
        setHeaderTitle("Inventory");
        if (userId) fetchProducts();
    }, [userId, currentLicense, currentLocation, setHeaderTitle, licensesLoaded]);

    useEffect(() => {
        if (!loadProducts && currentPhase === 2 && coas.length > 0) {
            checkProductsTutorial(); 
        }
    }, [loadProducts, currentPhase, coas.length, checkProductsTutorial]);

    useEffect(() => {
        if (!currentLicense?.licenseNo) return; 
        const locationParam = currentLocation?.locationId ? `&locationId=${currentLocation.locationId}` : "";
        apiClient
            .get(`/permissions/my-permissions?licenseNo=${currentLicense.licenseNo}${locationParam}`)
            .then((res) => {
                setMyPermissions(res.data);
                localStorage.setItem("currentPermissions", JSON.stringify(res.data));
            })
            .catch(() => setMyPermissions({ can_upload_coa: 1, can_view_showroom: 1, can_see_coa_options: 1 }));
    }, [currentLicense, currentLocation]);

    // ── Pending transfer request count (for badge) ────────────────────────────
 
    useEffect(() => {
        if (!currentLicense?.licenseNo) return;
        apiClient
            .get(`/transfer-requests?licenseNo=${currentLicense.licenseNo}&status=requested`)
            .then((res) => setPendingRequestCount(res.data?.count || 0))
            .catch(() => setPendingRequestCount(0));
        apiClient
            .get(`/location-requests?licenseNo=${currentLicense.licenseNo}&status=requested`)
            .then((res) => setPendingLocationRequestCount(res.data?.count || 0))
            .catch(() => setPendingLocationRequestCount(0));
    }, [currentLicense]);

    // ── Panel toggle ──────────────────────────────────────────────────────────

    const handlePanelToggle = () => {
        setPanelOpen((prev) => {
            const next = !prev;
            localStorage.setItem("dashboard_panel_open", String(next));
            return next;
        });
    };

    // ── Filter apply handler ──────────────────────────────────────────────────

    const handleApplyFilters = (filters) => {
        setAppliedFilters(filters);
        localStorage.setItem("dashboard_filter_searchTerm",  filters.searchTerm  ?? "");
        localStorage.setItem("dashboard_filter_color",       filters.selectedColor ?? "");
        localStorage.setItem("dashboard_filter_productType", filters.selectedProductType ?? "");
        localStorage.setItem("dashboard_filter_fullPanel",   filters.fullPanelFilter ?? "");
        localStorage.setItem("dashboard_filter_startDate",   filters.startDate ?? "");
        localStorage.setItem("dashboard_filter_endDate",     filters.endDate ?? "");
    };

    // ── Publish / unpublish handlers ──────────────────────────────────────────

    const handlePublish = (product) => {
        const colorLower = (product.color || "").toLowerCase();
        const validColor = colorLower !== "no color" && colorLower !== "synthetic";
        if (validColor && isWholesaleEligible(product)) {
            setPublishModalCoa(product);
        }
    };

    const handleUnpublish = (product) => {
        setUnpublishConfirmCoa(product);
    };

    async function confirmUnpublish() {
        try {
            await apiClient.delete(`/showroom/remove/${unpublishConfirmCoa.id}`);
        } catch (e) {
            console.error(e);
        } finally {
            setUnpublishConfirmCoa(null);
            fetchCoas();
        }
    }

    // ── Export handler ────────────────────────────────────────────────────────

    const handleExport = async (selectedFields) => {
        if (!currentLicense?.licenseNo) return;
        try {
            const locationParam = currentLocation?.locationId
                ? `&locationId=${currentLocation.locationId}`
                : "";
            const res = await apiClient.get(
                `/products/export?licenseNo=${currentLicense.licenseNo}${locationParam}`
            );
            const allData = Array.isArray(res.data) ? res.data : [];

            const filtered = allData.filter(
                (coa) =>
                    coa.product_name &&
                    coa.product_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
                    (selectedColor === "" || (coa.color || "").toLowerCase() === selectedColor) &&
                    (startDate === "" || new Date(coa.created_at) >= new Date(startDate)) &&
                    (endDate === "" || new Date(coa.created_at) <= new Date(endDate + "T23:59:59")) &&
                    (selectedProductType === "" || (coa.product_type || "").toLowerCase() === selectedProductType) &&
                    (fullPanelFilter === "" ||
                        (fullPanelFilter === "full" && isCOAComplete(coa)) ||
                        (fullPanelFilter === "not-full" && !isCOAComplete(coa)))
            );

            exportProductData(filtered, currentLicense.company || "export", selectedFields);
        } catch (err) {
            console.error("Export failed:", err);
        }
    };

    // ── Filtered COA list ─────────────────────────────────────────────────────

    const { searchTerm, selectedColor, selectedProductType, fullPanelFilter, startDate, endDate } = appliedFilters;

    const filteredCoas = useMemo(() => {
        return coas.filter(
            (coa) =>
                coa.product_name &&
                coa.product_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
                (selectedColor === "" || (coa.color || "").toLowerCase() === selectedColor) &&
                (startDate === "" || new Date(coa.created_at) >= new Date(startDate)) &&
                (endDate === "" || new Date(coa.created_at) <= new Date(endDate + "T23:59:59")) &&
                (selectedProductType === "" || (coa.product_type || "").toLowerCase() === selectedProductType) &&
                (fullPanelFilter === "" ||
                    (fullPanelFilter === "full" && isCOAComplete(coa)) ||
                    (fullPanelFilter === "not-full" && !isCOAComplete(coa)))
        );
    }, [coas, searchTerm, selectedColor, startDate, endDate, selectedProductType, fullPanelFilter]);

    // ── KPI summary (follows filters) ────────────────────────────────────────

    const inventoryKpis = useMemo(() => {
        const total = filteredCoas.length;
        if (total === 0) return null;

        let fullPanelCount = 0;
        let totalTerps = 0;
        let showroomCount = 0;

        for (const coa of filteredCoas) {
            if (isCOAComplete(coa)) fullPanelCount++;
            totalTerps += calcTotalTerps(coa);
            if (coa.published) showroomCount++;
        }

        return { total, showroomCount, fullPanelCount, avgTerps: totalTerps / total };
    }, [filteredCoas]);

    // ── Color distribution (follows filters) ─────────────────────────────────

    const colorDist = useMemo(() => {
        const counts = { yellow: 0, green: 0, blue: 0, navy: 0, purple: 0 };
        for (const coa of filteredCoas) {
            const pt = (coa.product_type || "").toLowerCase();
            if (pt !== "flower" && pt !== "pre-roll") continue;
            const c = (coa.color || "").toLowerCase();
            if (c in counts) counts[c]++;
        }
        return counts;
    }, [filteredCoas]);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div>
            <div className="dash">
                {loadProducts && <Loading />}
                {!loadProducts && (
                    <div className="container-dash">
                        {/* ── Toolbar ── */}
                        <div className="dashboard-toolbar">
                            <button
                                className={`dash-filter-btn${panelOpen ? " dash-filter-btn--active" : ""}`}
                                onClick={handlePanelToggle}
                                aria-label={panelOpen ? "Close filters" : "Open filters"}
                            >
                                <svg className="dash-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
                                </svg>
                                <span className="dash-filter-label">{panelOpen ? "Hide Filters" : "Filters"}</span>
                            </button>

                            <div className="dash-secondary-actions">
                                <span className="dash-tooltip-wrap" data-tip="Export filtered inventory to CSV" data-tutorial="export-btn">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        icon={<svg className="dash-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>}
                                        className="dash-action-btn"
                                        onClick={() => setShowExportModal(true)}
                                        disabled={!currentLicense?.licenseNo}
                                    >
                                        <span className="dash-action-label">Export</span>
                                    </Button>
                                </span>
                                <span className="dash-tooltip-wrap" data-tip="Log a weigh-in for inventory" data-tutorial="weigh-in-btn">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        icon={<svg className="dash-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="M7 21h10"/><path d="M12 3v18"/><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/></svg>}
                                        className="dash-action-btn"
                                        onClick={() => setShowWeighInModal(true)}
                                        disabled={!currentLicense?.licenseNo}
                                    >
                                        <span className="dash-action-label">Weigh-In</span>
                                    </Button>
                                </span>
                                <span className="dash-tooltip-wrap" data-tip="Download compliance badge QR codes" data-tutorial="qr-code-btn">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        icon={<svg className="dash-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect width="5" height="5" x="3" y="3" rx="1"/><rect width="5" height="5" x="16" y="3" rx="1"/><rect width="5" height="5" x="3" y="16" rx="1"/><path d="M21 16h-3a2 2 0 0 0-2 2v3"/><path d="M21 21v.01"/><path d="M12 7v3a2 2 0 0 1-2 2H7"/><path d="M3 12h.01"/><path d="M12 3h.01"/><path d="M12 16v.01"/><path d="M16 12h1"/><path d="M21 12v.01"/><path d="M12 21v-1"/></svg>}
                                        className="dash-action-btn"
                                        onClick={() => setShowQRModal(true)}
                                        disabled={!currentLicense?.licenseNo}
                                    >
                                        <span className="dash-action-label">QR Code</span>
                                    </Button>
                                </span>
                                <span
                                    className="dash-tooltip-wrap"
                                    data-tutorial="generate-menu-btn"
                                    data-tip={
                                        !currentLicense?.licenseNo
                                            ? "Select a license to use this feature"
                                            : currentLocation?.locationId
                                              ? "Generate a product menu for this location"
                                              : "Generate a product menu from master inventory"
                                    }
                                    {...(!currentLicense?.licenseNo ? { "data-tip-type": "warning" } : {})}
                                >
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        icon={<svg className="dash-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>}
                                        className="dash-action-btn"
                                        onClick={() => setShowGenerateMenuModal(true)}
                                        disabled={!currentLicense?.licenseNo}
                                    >
                                        <span className="dash-action-label">Menu</span>
                                    </Button>
                                </span>
 
                                {/* Upload Invoice — disabled until feature is ready
                                {myPermissions?.can_upload_invoice === 1 && (
                                    <span className="dash-tooltip-wrap" data-tip="Upload invoice to create products" data-tutorial="invoice-upload-btn">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            icon={<svg className="dash-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 12 15 15"/></svg>}
                                            className="dash-action-btn"
                                            onClick={() => setShowInvoiceUploadModal(true)}
                                            disabled={!currentLicense?.licenseNo}
                                        >
                                            <span className="dash-action-label">Upload Invoice</span>
                                        </Button>
                                    </span>
                                )}
                                */} 

                                <span className="dash-tooltip-wrap" data-tip="Move inventory between locations and master inventory" data-tutorial="manage-transfers-btn">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    className="dash-action-btn"
                                    onClick={() => setShowLocationTransfersDrawer(true)}
                                >
                                    <span className="dash-action-label">Manage Transfers</span>
                                    {pendingLocationRequestCount > 0 && (
                                        <span className="dashboard-requests-badge">
                                            {pendingLocationRequestCount}
                                        </span> 
                                    )}
                                </Button>
                                </span>
                            </div>  

                            {currentLocation?.locationId ? (
                                <span className="dash-tooltip-wrap" data-tip="Request products from license inventory" data-tutorial="request-products-btn">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    icon={<svg className="dash-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>}
                                    className="dash-create-btn"
                                    onClick={() => setShowRequestProductsModal(true)}
                                >
                                    <span className="dash-action-label">Request Products</span>
                                </Button>
                                </span>
                            ) : (
                                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                                <span className="dash-tooltip-wrap" data-tip="Create a mix from multiple flower products">
                                <Button 
                                    variant="secondary"  
                                    size="sm"
                                    icon={<svg className="dash-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>}
                                    className="dash-action-btn"
                                    onClick={() => setShowCreateMixModal(true)}
                                    disabled={!currentLicense?.licenseNo}
                                >
                                    <span className="dash-action-label">Create Mix</span>
                                </Button>   
                                </span>
                                <span className="dash-tooltip-wrap" data-tip="Create a new product" data-tutorial="create-product-btn">
                                <Button
                                    variant="primary"
                                    size="sm" 
                                    icon={<svg className="dash-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>}
                                    className="dash-create-btn"
                                    onClick={() => setShowCreateProductModal(true)}
                                >
                                    <span className="dash-action-label">New Product</span>
                                </Button>
                                </span>
                                </div>
                            )}
                        </div>

                        {/* ── Content area ── */}
                        <div className="dashboard-content-area">
                            <DashboardFilterPanel
                                isOpen={panelOpen}
                                appliedFilters={appliedFilters}
                                onApply={handleApplyFilters}
                                productTypes={productTypes}
                                colors={colors}
                            />

                            <div className="dashboard-container-wrapper" data-tutorial="inventory-table">
                                {/* Header bar */}
                                <div className="dashboard-COA-header">
                                    <span className="coa-tite">{licensesLoaded ? `${currentLicense?.company} Inventory` : ""}</span>
                                    {currentLocation?.locationId && (
                                        <span className="coa-header-location-badge">
                                            <svg
                                                width="11"
                                                height="11"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                aria-hidden="true"
                                            >
                                                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                                                <circle cx="12" cy="10" r="3" />
                                            </svg>
                                            {currentLocation.locationName}
                                        </span>
                                    )}
                                </div>

                                {/* Summary bar */}
                                {inventoryKpis && (
                                    <div className="dashboard-summary-bar" data-tutorial="dashboard-summary-bar">
                                        <div className="dsb-color-bar-row">
                                            <div className="dsb-color-bar">
                                                {(() => {
                                                    const colorTotal = COLOR_ORDER.reduce(
                                                        (sum, { key }) => sum + colorDist[key],
                                                        0
                                                    );
                                                    return COLOR_ORDER.map(({ key, label }) => {
                                                        const count = colorDist[key];
                                                        if (count === 0) return null;
                                                        const pct = colorTotal > 0 ? (count / colorTotal) * 100 : 0;
                                                        const isActive = selectedColor === key;
                                                        return (
                                                            <div
                                                                key={key}
                                                                className={`dsb-color-bar-segment${isActive ? " dsb-color-bar-segment--active" : ""}`}
                                                                style={{
                                                                    width: `${pct}%`,
                                                                    backgroundColor: getColor(key),
                                                                    color: key === "navy" ? "#ffffff" : undefined,
                                                                }}
                                                                title={
                                                                    isActive
                                                                        ? `Clear ${label} filter`
                                                                        : `Filter by ${label}`
                                                                }
                                                                onClick={() =>
                                                                    handleApplyFilters({
                                                                        ...appliedFilters,
                                                                        selectedColor: isActive ? "" : key,
                                                                    })
                                                                }
                                                            >
                                                                <span className="dsb-color-legend-count">
                                                                    {count}
                                                                </span>
                                                            </div>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                        </div>
                                        <div className="dsb-kpis">
                                            <div className="dsb-kpi-box">
                                                <span className="dsb-kpi-value">{inventoryKpis.total}</span>
                                                <span className="dsb-kpi-label">Products</span>
                                            </div>
                                            <div className="dsb-kpi-box">
                                                <span className="dsb-kpi-value">{inventoryKpis.showroomCount}</span>
                                                <span className="dsb-kpi-label">On Marketplace</span>
                                            </div>
                                            <div className="dsb-kpi-box">
                                                <span className="dsb-kpi-value">
                                                    {inventoryKpis.fullPanelCount} / {inventoryKpis.total}
                                                </span>
                                                <span className="dsb-kpi-label">Full Panel</span>
                                            </div>
                                            <div className="dsb-kpi-box">
                                                <span className="dsb-kpi-value">
                                                    {inventoryKpis.avgTerps.toFixed(2)}%
                                                </span>
                                                <span className="dsb-kpi-label">Avg Terpenes</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Unified inventory table */}
                                <InventoryTable
                                    data={filteredCoas}
                                    columns={activeColumns}
                                    locations={locations}
                                    myPermissions={myPermissions}
                                    fetchCoas={fetchCoas}
                                    title={title}
                                    setCOAProcessing={setCOAProcessing}
                                    isLocationFiltered={!!currentLocation?.locationId}
                                    currentLocation={currentLocation}
                                    onPublish={handlePublish}
                                    onUnpublish={handleUnpublish}
                                    onOpenColumnPicker={() => setShowColumnPicker(true)}
                                    onLocationQtyClick={(product, locationId, locationName) =>
                                        setLocationTransfer({ product, locationId, locationName })
                                    }
                                    onCreatePreRoll={(product) => setPreRollSource(product)}
                                />
                            </div>
                        </div>

                        {/* Modals */}
                        {publishModalCoa && (
                            <CoaToShowroom
                                coa={publishModalCoa}
                                productId={publishModalCoa.id}
                                setPublishTab={() => setPublishModalCoa(null)}
                                fetchCoas={fetchCoas}
                            />
                        )}
                        {unpublishConfirmCoa && (
                            <ConfirmDialog
                                message={`Remove "${mixDisplayName(unpublishConfirmCoa.product_name, unpublishConfirmCoa.product_type, unpublishConfirmCoa.created_at)}" from the marketplace?`}
                                onConfirm={confirmUnpublish}
                                onCancel={() => setUnpublishConfirmCoa(null)}
                            />
                        )}
                        {showColumnPicker && (
                            <ColumnPickerModal
                                activeColumns={activeColumns}
                                onApply={setActiveColumns}
                                onClose={() => setShowColumnPicker(false)}
                                locations={locations}
                            />
                        )}
                        {showCreateProductModal && (
                            <CreateProductModal
                                onClose={() => setShowCreateProductModal(false)}
                                onSelectUpload={() => {
                                    setShowCreateProductModal(false);
                                    setShowStagedUploadModal(true);
                                }}
                                onSelectOrderTests={() => {
                                    setShowCreateProductModal(false);
                                    navigate("/LabOrders");
                                }}
                            />
                        )}
                        {showStagedUploadModal && (
                            <COAStagedUploadModal
                                onClose={() => setShowStagedUploadModal(false)}
                                fetchCoas={fetchCoas}
                                setCOAProcessing={setCOAProcessing}
                            />
                        )}
                        {showTransferRequestsModal && (
                            <TransferRequestsModal
                                licenseNo={currentLicense?.licenseNo}
                                onClose={() => setShowTransferRequestsModal(false)}
                                onCountChange={(count) => setPendingRequestCount(count)}
                            />
                        )}
                        {showLocationTransfersDrawer && (
                            <LocationTransfersDrawer
                                licenseNo={currentLicense?.licenseNo}
                                locations={locations}
                                canManage={myPermissions?.can_manage_location_transfers === 1}
                                isLocationScoped={!!currentLocation?.locationId}
                                locationId={currentLocation?.locationId}
                                locationName={currentLocation?.locationName}
                                onClose={() => setShowLocationTransfersDrawer(false)}
                                onCountChange={(count) => setPendingLocationRequestCount(count)}
                                onProductsRequested={() => fetchProducts()}
                            />
                        )}
                        {showRequestProductsModal && (
                            <RequestProductsModal
                                licenseNo={currentLicense?.licenseNo}
                                locationId={currentLocation?.locationId}
                                locationName={currentLocation?.locationName}
                                onClose={() => setShowRequestProductsModal(false)}
                                onSubmitted={() => fetchProducts()}
                            />
                        )}
                        {showExportModal && (
                            <ExportModal
                                onConfirm={(selectedFields) => {
                                    setShowExportModal(false);
                                    handleExport(selectedFields);
                                }}
                                onCancel={() => setShowExportModal(false)}
                            />
                        )}
                        {showQRModal && currentLicense?.licenseNo && (
                            <QRModal
                                licenseNo={currentLicense.licenseNo}
                                companyName={currentLicense.company || currentLicense.licenseNo}
                                locationId={currentLocation?.locationId || null}
                                onClose={() => setShowQRModal(false)}
                            />
                        )}
                        {showGenerateMenuModal && currentLicense?.licenseNo && (
                            <GenerateMenuModal
                                licenseNo={currentLicense.licenseNo}
                                licenseName={currentLicense.company || currentLicense.licenseNo}
                                locationId={currentLocation?.locationId || null}
                                locationName={currentLocation?.locationName}
                                onClose={() => setShowGenerateMenuModal(false)}
                            />
                        )}
                        {showInvoiceUploadModal && (
                            <InvoiceUploadModal
                                onClose={() => setShowInvoiceUploadModal(false)}
                            />
                        )}
                        {showWeighInModal && (
                            <WeighInModal
                                products={coas}
                                licenseNo={currentLicense?.licenseNo}
                                locationId={currentLocation?.locationId || null}
                                onClose={() => setShowWeighInModal(false)}
                                onSuccess={fetchProducts}
                            />
                        )}
                        {preRollSource && (
                            <CreatePreRollModal
                                sourceProduct={preRollSource}
                                licenseNo={currentLicense?.licenseNo}
                                locationId={currentLocation?.locationId || null}
                                onClose={() => setPreRollSource(null)}
                                onSuccess={fetchProducts}
                            />
                        )}
                        {showCreateMixModal && (
                            <CreateMixModal
                                licenseNo={currentLicense?.licenseNo}
                                locationId={currentLocation?.locationId || null}
                                onClose={() => setShowCreateMixModal(false)}
                                onSuccess={fetchProducts}
                            />
                        )}
                        {locationTransfer && (
                            <TransferWeightModal
                                coaId={locationTransfer.product.coa_id}
                                sourceLicenseNo={currentLicense?.licenseNo}
                                sourceLocationId={currentLocation?.locationId || null}
                                defaultTarget={{
                                    type: "location",
                                    locationId: locationTransfer.locationId,
                                    locationName: locationTransfer.locationName,
                                    licenseNo: currentLicense?.licenseNo,
                                    company: currentLicense?.company,
                                }}
                                onClose={() => setLocationTransfer(null)}
                                onSuccess={() => { setLocationTransfer(null); fetchProducts(); }}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
